import { useCallback, useLayoutEffect, useState } from "react";
import type { KeyboardEvent, RefObject } from "react";
import { findInputTriggerAtCaret } from "@/lib/input-trigger";
import { parseNewChatCommand } from "@/lib/new-chat-command";
import type { ComposerFieldHandle } from "./standalone-bar-field";
import {
  getFilteredSlashCommands,
  type SlashCommand,
} from "@/lib/slash-commands";

interface UseSlashCommandHandlerArgs {
  enabled: boolean;
  value: string;
  onChange: (value: string) => void;
  slashCommands?: SlashCommand[];
  onSlashCommand?: (command: SlashCommand) => void;
  /** Focus is restored to the textarea after popup-driven mutations. */
  inputRef: RefObject<ComposerFieldHandle | null>;
}

interface UseSlashCommandHandlerResult {
  slashOpen: boolean;
  slashQuery: string;
  slashIndex: number;
  selectSlashCommand: (command: SlashCommand) => void;
  /** Returns true if the input was consumed by a built-in command, so the
   *  caller skips message-submit. */
  tryDispatch: () => boolean;
  /** Keyboard navigation: returns true if the event was consumed. */
  handleKeyDown: (e: KeyboardEvent<HTMLElement>) => boolean;
}

/** Owns the slash-command popup state machine — open/close detection from
 *  the textarea contents, ↑/↓/Enter/Esc keynav, popup-select handling, and
 *  built-in command dispatch on Enter (when the popup is closed). */
export function useSlashCommandHandler({
  enabled,
  value,
  onChange,
  slashCommands,
  onSlashCommand,
  inputRef,
}: UseSlashCommandHandlerArgs): UseSlashCommandHandlerResult {
  const [slashOpen, setSlashOpen] = useState(false);
  const [slashQuery, setSlashQuery] = useState("");
  const [slashIndex, setSlashIndex] = useState(0);

  const hasCommands = (slashCommands?.length ?? 0) > 0;
  // Every draft change (typing, desktop paste, dictation, prefill, send)
  // re-reads "/query" at the committed caret, so no query goes stale.
  useLayoutEffect(() => {
    const trigger =
      enabled && hasCommands
        ? findInputTriggerAtCaret(value, inputRef.current, "/")
        : null;
    setSlashOpen(trigger !== null);
    setSlashQuery(trigger?.query ?? "");
    setSlashIndex(0);
  }, [enabled, hasCommands, inputRef, value]);

  const selectSlashCommand = useCallback(
    (command: SlashCommand) => {
      setSlashOpen(false);
      setSlashQuery("");
      if (command.type === "command") {
        const arg = command.accepts_arg
          ? value.trim().split(/\s+/).slice(1).join(" ") || undefined
          : undefined;
        command.handler?.(arg);
        onChange("");
      } else if (command.type === "skill") {
        const slashIdx = value.lastIndexOf("/" + slashQuery);
        if (slashIdx !== -1) {
          const before = value.slice(0, slashIdx);
          onChange(before + "/" + command.name + " ");
        } else {
          onChange("/" + command.name + " ");
        }
        command.handler?.();
      }
      onSlashCommand?.(command);
      inputRef.current?.focus();
    },
    [value, slashQuery, onChange, onSlashCommand, inputRef],
  );

  /**
   * Try to dispatch the input as a built-in slash command (e.g. `/max 5`).
   * Returns true when the input was consumed by a command handler so the
   * caller should skip the normal message-submit path.
   *
   * Note: this also intercepts non-arg commands typed directly (e.g. `/tidy`
   * with the popup dismissed). Any text starting with a known command name
   * is treated as a command, not a chat message — matching the popup-select
   * path. Unknown commands fall through and are sent as messages.
   */
  const tryDispatch = useCallback((): boolean => {
    if (!slashCommands || slashCommands.length === 0) return false;
    // `/new <request>` is the first message of a new chat: the composer's
    // submit path starts the chat and sends it.
    if (parseNewChatCommand(value)) return false;
    const trimmed = value.trim();
    if (!trimmed.startsWith("/")) return false;
    const [first, ...rest] = trimmed.slice(1).split(/\s+/);
    const name = first?.toLowerCase();
    if (!name) return false;
    const command = slashCommands.find(
      (c) => c.type === "command" && c.name === name,
    );
    if (!command) return false;
    const arg = command.accepts_arg ? rest.join(" ") || undefined : undefined;
    command.handler?.(arg);
    onChange("");
    setSlashOpen(false);
    setSlashQuery("");
    return true;
  }, [slashCommands, value, onChange]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>): boolean => {
      if (!slashOpen || !slashCommands) return false;
      const filteredCmds = getFilteredSlashCommands(slashCommands, slashQuery);
      if (filteredCmds.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSlashIndex((i) => Math.min(i + 1, filteredCmds.length - 1));
          return true;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSlashIndex((i) => Math.max(i - 1, 0));
          return true;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          const selected = filteredCmds[slashIndex];
          if (selected) selectSlashCommand(selected);
          return true;
        }
      }
      if (e.key === "Escape") {
        setSlashOpen(false);
        return true;
      }
      return false;
    },
    [slashOpen, slashCommands, slashQuery, slashIndex, selectSlashCommand],
  );

  return {
    slashOpen,
    slashQuery,
    slashIndex,
    selectSlashCommand,
    tryDispatch,
    handleKeyDown,
  };
}
